import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import { Box } from "@mui/material";
import DataTable from "../DataTable.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUserActions } from "../../hooks/useUserActions.jsx";
import PropTypes from "prop-types";
import {
  AddCircleOutlineOutlined,
  UploadFile as UploadFileIcon,
  AssignmentIndOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { TableEntityCell } from "../../theme/components/index.js";

// Subtle neutral pill for the role column (dummy: light-grey chip).
function RolePill({ label }) {
  if (!label) return "—";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "10px",
        py: "3px",
        borderRadius: "7px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: "#eef1f5",
        color: "#475569",
      }}
    >
      {label}
    </Box>
  );
}

// Coloured dot + label status (dummy: Aktiv=green, Abwesend=amber, Inaktiv=grey).
const STATUS_TONE = {
  aktiv: { c: "#16a34a", bg: "#dcfce7" },
  active: { c: "#16a34a", bg: "#dcfce7" },
  abwesend: { c: "#b45309", bg: "#fef3c7" },
  absent: { c: "#b45309", bg: "#fef3c7" },
  inaktiv: { c: "#64748b", bg: "#f1f5f9" },
  inactive: { c: "#64748b", bg: "#f1f5f9" },
};
function StatusPillDot({ value }) {
  if (value === undefined || value === null || value === "") return "—";
  const tone = STATUS_TONE[String(value).toLowerCase()] || {
    c: "#64748b",
    bg: "#f1f5f9",
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "10px",
        py: "3px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: tone.bg,
        color: tone.c,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: tone.c }} />
      {String(value).charAt(0).toUpperCase() + String(value).slice(1)}
    </Box>
  );
}

export default function UsersTable({
  onRowEdit = () => {},
  onClickNew = () => {},
  onClickImport = () => {},
  onClickAssignments,
  pageTitle,
  pageSubtitle,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const { state: usersState, setState: setUsersState } = useUserActions();

  const tableKey = "users";
  const storageKey = "v2_table_users";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const is_sys_admin = user?.role_names?.includes("sysadmin");
  const can_manage_users = !!is_sys_admin;

  const rows = usersState?.data?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Benutzer",
        route: `/data/users`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "email", order: "asc" }],
        },
        onStateChange: setUsersState,
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toolbarActions = [
    {
      type: "button",
      label: "Neuer Benutzer",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    ...(can_manage_users
      ? [
          {
            type: "button",
            label: "Benutzer importieren",
            icon: <UploadFileIcon />,
            onClick: onClickImport,
            variant: "tertiary",
          },
        ]
      : []),
    ...(can_manage_users && onClickAssignments
      ? [
          {
            type: "button",
            label: "Vertragszuordnung",
            icon: <AssignmentIndOutlined />,
            onClick: onClickAssignments,
            variant: "tertiary",
          },
        ]
      : []),
  ];

  const columns = [
    {
      accessorFn: (row) =>
        `${row.vorname || ""} ${row.nachname || ""}`.trim() || row.email || "—",
      accessorKey: "nachname",
      accessorKeys: ["vorname", "nachname"],
      header: "Name",
      size: 250,
      Cell: ({ row }) => {
        const name =
          `${row.original?.vorname || ""} ${row.original?.nachname || ""}`.trim() ||
          row.original?.email ||
          "—";
        return <TableEntityCell title={name} avatar={{ label: name }} />;
      },
    },
    {
      accessorKey: "email",
      header: "E-Mail-Adresse",
      size: 250,
      Cell: ({ row }) =>
        row.original?.email ? (
          <Box component="span" sx={{ color: "primary.main", fontWeight: 500 }}>
            {row.original.email}
          </Box>
        ) : (
          "—"
        ),
    },
    {
      accessorKey: "rolle",
      header: "Rolle",
      // `displayname_rolle` is the BE's UserRole::displayName(); the raw `rolle`
      // is the internal key (e.g. "makler_sachbearbeitung") and must not be shown to users.
      Cell: ({ row }) => (
        <RolePill label={row.original?.displayname_rolle || row.original?.rolle} />
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      Cell: ({ row }) => <StatusPillDot value={row.original?.status} />,
    },
    {
      accessorFn: (row) =>
        `${row.kunde_name1 || ""} ${row.kunde_name2 || ""}`.trim(),
      accessorKey: ["kunde_name1"],
      accessorKeys: ["kunde_name1", "kunde_name2"],
      header: "Kunde",
    },
    // {
    //   id: "actions",
    //   header: "Aktionen",
    //   size: 120,
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   Cell: ({ row }) => (
    //     <>
    //       {can_manage_users && (
    //         <IconButton
    //           size="small"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             onRowEdit(row.original);
    //           }}
    //           title="Benutzer bearbeiten"
    //         >
    //           <EditIcon />
    //         </IconButton>
    //       )}
    //     </>
    //   ),
    // },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      pageTitle={pageTitle}
      pageSubtitle={pageSubtitle}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar={true}
      toolbarActions={toolbarActions}
      onRowClick={(row) => {
        if (can_manage_users && row?.id) {
          navigate(`/admin/users/${row.id}`);
        }
      }}
    />
  );
}

UsersTable.propTypes = {
  onRowEdit: PropTypes.func,
  onClickNew: PropTypes.func,
  onClickImport: PropTypes.func,
  onClickAssignments: PropTypes.func,
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
};
